
import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Save } from 'lucide-react';

interface AutoUserEmailTemplateTabProps {
  onSaved: () => void;
}

interface EmailTemplateFormValues {
  subject: string;
  content: string;
}

const DEFAULT_TEMPLATE = {
  subject: 'Join our Referral Program - Free of Charge!',
  content: `
Dear {name},

Thank you for your recent purchase with us! We're excited to invite you to join our Referral Program, completely free of charge.

By joining our program, you can:
- Refer friends and family to our store
- Earn points for every successful referral
- Redeem points for cash rewards or discounts on products
- Track your earnings in your personalized dashboard

To get started, simply click the button below to accept this invitation:

{invite_button}

If you have any questions or need assistance, feel free to contact our support team.

Thank you for being a valued customer!

Best regards,
The {company} Team
  `.trim()
};

const AutoUserEmailTemplateTab: React.FC<AutoUserEmailTemplateTabProps> = ({ onSaved }) => {
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();
  
  const { register, handleSubmit, reset, formState: { errors, isDirty } } = useForm<EmailTemplateFormValues>({
    defaultValues: DEFAULT_TEMPLATE
  });
  
  useEffect(() => {
    // Load template from localStorage
    const loadTemplate = () => {
      try {
        const savedTemplate = localStorage.getItem('autoUserEmailTemplate');
        if (savedTemplate) {
          const template = JSON.parse(savedTemplate);
          reset(template);
        }
      } catch (error) {
        console.error('Error loading email template:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadTemplate();
  }, [reset]);
  
  const onSubmit = async (data: EmailTemplateFormValues) => {
    try {
      setIsLoading(true);
      
      // Save template to localStorage
      localStorage.setItem('autoUserEmailTemplate', JSON.stringify(data));
      
      toast({
        title: "Template saved",
        description: "Auto user email template has been saved successfully.",
      });
      
      onSaved();
    } catch (error) {
      console.error('Error saving email template:', error);
      toast({
        title: "Error",
        description: "Failed to save email template. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const resetToDefault = () => {
    reset(DEFAULT_TEMPLATE);
    toast({
      title: "Template reset",
      description: "Email template has been reset to default.",
    });
  };
  
  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium">Auto User Email Template</h3>
        <p className="text-sm text-muted-foreground">
          Customize the email template sent to new users from e-commerce integrations.
        </p>
      </div>
      
      <form onSubmit={handleSubmit(onSubmit)}>
        <Card>
          <CardHeader>
            <CardTitle>Welcome Email Template</CardTitle>
            <CardDescription>
              This email will be sent to new users who are automatically added to your referral program from connected e-commerce platforms.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="subject">Email Subject</Label>
              <Input
                id="subject"
                {...register("subject", {
                  required: "Subject is required"
                })}
              />
              {errors.subject && (
                <p className="text-sm text-destructive">{errors.subject.message}</p>
              )}
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="content">Email Content</Label>
              <Textarea
                id="content"
                rows={15}
                {...register("content", {
                  required: "Content is required"
                })}
              />
              {errors.content && (
                <p className="text-sm text-destructive">{errors.content.message}</p>
              )}
              
              <div className="bg-muted p-3 rounded-md text-xs mt-2">
                <p className="font-medium mb-1">Available Variables:</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li><code>{'{name}'}</code> - The customer's name</li>
                  <li><code>{'{email}'}</code> - The customer's email address</li>
                  <li><code>{'{company}'}</code> - Your company name</li>
                  <li><code>{'{invite_button}'}</code> - The invitation acceptance button</li>
                  <li><code>{'{current_date}'}</code> - The current date</li>
                </ul>
              </div>
            </div>
          </CardContent>
          <CardFooter className="flex justify-between">
            <Button
              type="button"
              variant="outline"
              onClick={resetToDefault}
            >
              Reset to Default
            </Button>
            <Button
              type="submit"
              disabled={!isDirty || isLoading}
              className="flex items-center"
            >
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              <Save className="mr-2 h-4 w-4" />
              Save Template
            </Button>
          </CardFooter>
        </Card>
      </form>
    </div>
  );
};

export default AutoUserEmailTemplateTab;
